import React, { useState } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined,
    CheckOutlined,
    DeleteOutlined,
    DollarOutlined
} from "@ant-design/icons";
import { Col, Row, Button, Tooltip } from "antd";
import CommonCard from "../../../global/card";
import CommonDialog from "../../../global/dialog";
import { cn } from "../../../../utils/cn";
import { formatDateTime } from "../../../../utils/functions";

const EnhancedLeadsImportedViaFormDetails = ({
    leadDetail,
    handleAcceptReject
}) => {
    const [toggleModal, setToggleModal] = useState({
        show: false,
        type: "",
    });

    if (!leadDetail) return null;

    const onActionConfirm = async (type) => {
        await handleAcceptReject(leadDetail._id, type);
        setToggleModal({ show: false, type: "" });
    };

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Budget: <DollarOutlined />,
            "Notes/Inquiry": <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" } })}
            <span style={{ fontSize: "clamp(13px, 4vw, 15px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const InfoItem = ({ label, value }) => (
        <Col xs={24} sm={12}>
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                    <span style={{ color: "var(--primary-color, #1890ff)" }}>
                        {getIconForField(label)}
                    </span>
                    {label}
                </span>
                <span className="text-sm font-semibold text-gray-900">{value || "—"}</span>
            </div>
        </Col>
    );

    return (
        <div className="sm:!p-6 p-2 w-full min-h-full overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
                title={
                    <div className="flex items-center gap-2">
                        <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "clamp(16px, 5vw, 18px)" }} />
                        <span style={{ fontSize: "clamp(14px, 5vw, 16px)", fontWeight: 600 }}>Lead Details</span>
                    </div>
                }
                extra={
                    <div className="flex gap-2">
                        <Tooltip title="Accept">
                            <Button
                                type="primary"
                                className="bg-green-500 hover:bg-green-600 border-green-500 hover:border-green-600 !h-8 sm:!h-9 px-3 sm:px-4 text-xs sm:text-sm font-medium transition-all"
                                icon={<CheckOutlined />}
                                onClick={() => setToggleModal({ show: true, type: "ACCEPT" })}
                            >
                                Accept
                            </Button>
                        </Tooltip>
                        <Tooltip title="Reject">
                            <Button
                                danger
                                className="!h-8 sm:!h-9 px-3 sm:px-4 text-xs sm:text-sm font-medium transition-all"
                                icon={<DeleteOutlined />}
                                onClick={() => setToggleModal({ show: true, type: "REJECT" })}
                            >
                                Reject
                            </Button>
                        </Tooltip>
                    </div>
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Photo Section (if available) */}
                    {leadDetail.photo && (
                        <Col span={24} className="border-b border-gray-200 py-4 flex justify-center">
                            <div className="relative group">
                                <img
                                    src={leadDetail.photo}
                                    alt="Lead"
                                    className="size-20 sm:size-24 rounded-full object-cover border-2 border-primary/10 p-1 transition-transform group-hover:scale-105"
                                />
                                <div className="absolute inset-0 rounded-full bg-primary/5 animate-pulse -z-1" />
                            </div>
                        </Col>
                    )}

                    {/* Basic Information Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <SectionHeader icon={<UserOutlined />} title="Basic Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Name"
                                value={leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`}
                            />
                            <InfoItem label="Email" value={leadDetail?.email} />
                            <InfoItem label="Phone" value={leadDetail?.phone} />
                            <InfoItem label="Imported At" value={formatDateTime(leadDetail?.dateAsPerUserTimezone)} />
                        </Row>
                    </Col>

                    {/* Event Information Section */}
                    <Col span={24} className={cn("py-4", leadDetail?.inquiry && "border-b border-gray-200")}>
                        <SectionHeader icon={<CalendarOutlined />} title="Event Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem label="Event Name" value={leadDetail?.eventName} />
                            <InfoItem label="Event Type" value={leadDetail?.eventType} />
                            <InfoItem label="Event Date" value={leadDetail?.eventDate} />
                            <InfoItem label="Guest Count" value={leadDetail?.guestCount} />
                            <InfoItem label="Budget" value={leadDetail?.budget} />
                            <InfoItem label="Company" value={leadDetail?.company} />
                            <InfoItem label="Imported At" value={formatDateTime(leadDetail?.createdAt)} />
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="pt-4">
                            <SectionHeader icon={<InfoCircleOutlined />} title="Notes/Inquiry" />
                            <div className="bg-white p-3 sm:p-4 rounded-lg border border-gray-100 italic shadow-sm">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                            </div>
                        </Col>
                    )}
                </Row>
            </CommonCard>

            {toggleModal.show && (
                <CommonDialog
                    title={toggleModal.type === "ACCEPT" ? "Accept Lead" : "Reject Lead"}
                    description={
                        toggleModal.type === "ACCEPT"
                            ? "Are you sure you want to accept this lead?"
                            : "Are you sure you want to reject this lead? This action cannot be undone."
                    }
                    open={toggleModal.show}
                    setOpen={() => setToggleModal({ show: false, type: "" })}
                    onOk={() => onActionConfirm(toggleModal.type)}
                    onCancel={() => setToggleModal({ show: false, type: "" })}
                />
            )}
        </div>
    );
};

export default EnhancedLeadsImportedViaFormDetails;
